import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import GlassPanel from '@/components/ui/GlassPanel';
import BeamProgress from '@/components/ui/BeamProgress';
import BrandLogo from '@/components/ui/BrandLogo';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import { mockTransactions, savingsGoals, upcomingBills } from '@/data/mock';

const { 
  FiZap, FiTarget, FiCalendar, FiPieChart, 
  FiCheckCircle, FiAlertCircle, FiInfo, FiClock,
  FiArrowRight, FiFilter, FiActivity, FiHelpCircle,
  FiChevronDown, FiShield, FiUsers, FiCpu, FiLayout,
  FiDatabase, FiSmartphone
} = FiIcons;

export default function Demo() {
  const [activeStep, setActiveStep] = useState(0);
  const [simulatedState, setSimulatedState] = useState('success');
  const [openFaq, setOpenFaq] = useState(null);

  const steps = [
    { title: 'Neural Bridge', desc: 'Securely map your household liquidity streams into the cockpit without raw credential exposure.', icon: FiCpu },
    { title: 'Sector Calibration', desc: 'Define your primary spending envelopes: Sustenance, Habitat, Transport, and Recreation.', icon: FiTarget },
    { title: 'Trajectory Sync', desc: 'Project all upcoming monthly liabilities onto the 30-day temporal rail.', icon: FiCalendar },
    { title: 'Beam Management', desc: 'Set burn-rate thresholds for each sector to ensure zero-deficit cycles.', icon: FiActivity },
    { title: 'Overflow Routing', desc: 'Automatically route unspent liquidity into your prioritized savings planets.', icon: FiZap },
    { title: 'Active Telemetry', desc: 'Monitor live spending beams and receive proactive over-burn alerts.', icon: FiLayout },
    { title: 'Audit Reports', desc: 'Generate high-fidelity reports to optimize your next cycle trajectory.', icon: FiPieChart },
  ];

  const faqs = [
    { q: "How do I initialize my first cycle?", a: "Enter your total available liquidity in the Cockpit and allocate it across your primary envelopes. The system will then begin tracking your burn rate automatically." },
    { q: "Is this simulation using live bank data?", a: "No. This demo uses high-fidelity dummy records to showcase the interface. In a production environment, data is synced via secure neural bridges." },
    { q: "What are Spending Planets?", a: "Spending Planets are visual representations of your savings goals. The larger the planet, the closer you are to reaching your target orbit." },
    { q: "Does BudgetBeam provide financial advice?", a: "Strictly no. BudgetBeam is a household organization tool. We provide tracking and visualization, not investment or legal guidance." },
    { q: "Can I use this on multiple devices?", a: "Yes. Your financial cockpit is synced across all authenticated neural interfaces, including mobile and desktop nodes." },
    { q: "What happens if I over-burn a sector?", a: "The sector's beam will transition to a high-intensity red state, and the cockpit will suggest re-routing liquidity from other non-essential sectors." }
  ];

  return (
    <div className="space-y-20 pb-16 animate-in fade-in duration-1000">
      
      {/* 1. Overview / Product Identity */}
      <section className="relative grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="space-y-8">
          <div className="inline-flex items-center gap-3 px-4 py-2 bg-bb-gold/5 border border-bb-gold/20 rounded-full">
            <SafeIcon icon={FiInfo} className="text-bb-gold" size={14} />
            <span className="text-[10px] font-mono text-bb-gold uppercase tracking-[0.2em]">Product Overview</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white tracking-tight leading-[1.1]">
            Master Your <br/>
            <span className="text-bb-gold">Financial Trajectory.</span>
          </h1>
          <p className="text-lg text-gray-400 leading-relaxed max-w-xl">
            BudgetBeam is a high-fidelity visual cockpit for household liquidity. We eliminate spending blindness by replacing complex spreadsheets with intuitive <b>Envelope Beams</b> and <b>Savings Planets</b>.
          </p>
          <div className="flex flex-wrap gap-4 pt-4">
            <div className="flex items-center gap-3 px-5 py-3 bg-white/5 border border-white/10 rounded-2xl group hover:border-bb-accent/30 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-bb-accent/10 flex items-center justify-center text-bb-accent">
                <SafeIcon icon={FiShield} />
              </div>
              <div>
                <p className="text-xs font-bold text-white">Trustless Bridge</p>
                <p className="text-[10px] text-gray-500 font-mono">No Private Keys Required</p>
              </div>
            </div>
            <div className="flex items-center gap-3 px-5 py-3 bg-white/5 border border-white/10 rounded-2xl group hover:border-bb-gold/30 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-bb-gold/10 flex items-center justify-center text-bb-gold">
                <SafeIcon icon={FiLayout} />
              </div>
              <div>
                <p className="text-xs font-bold text-white">Visual Cockpit</p>
                <p className="text-[10px] text-gray-500 font-mono">Real-time Telemetry</p>
              </div>
            </div>
          </div>
        </div>

        {/* Brand Illustration Mask */}
        <div className="relative aspect-square">
          <div className="absolute inset-0 bg-gradient-to-br from-bb-gold/20 to-bb-accent/20 rounded-[3rem] blur-3xl opacity-30 animate-pulse"></div>
          <GlassPanel className="h-full w-full flex items-center justify-center border-white/10 overflow-hidden relative group">
             {/* Custom CSS/SVG Illustration representing the product */}
             <div className="relative w-64 h-64 scale-125 md:scale-150">
                {/* Central B Logo */}
                <div className="absolute inset-0 flex items-center justify-center">
                   <BrandLogo size="lg" className="opacity-80" />
                </div>
                {/* Orbiting Elements */}
                <motion.div 
                  animate={{ rotate: 360 }} 
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-0 border border-white/5 rounded-full"
                >
                   <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3 h-3 bg-bb-gold rounded-full shadow-[0_0_15px_#D4AF37]"></div>
                </motion.div>
                <motion.div 
                  animate={{ rotate: -360 }} 
                  transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-8 border border-white/5 rounded-full"
                >
                   <div className="absolute bottom-0 right-1/2 translate-x-1/2 w-2 h-2 bg-bb-accent rounded-full shadow-[0_0_10px_#00F0FF]"></div>
                </motion.div>
             </div>
             {/* UI Card Floating in Visual */}
             <div className="absolute bottom-8 right-8 w-48 p-4 bg-bb-panel/80 border border-white/10 rounded-xl backdrop-blur-xl shadow-2xl transform rotate-3 group-hover:rotate-0 transition-transform duration-500">
                <div className="flex justify-between items-center mb-3">
                  <div className="w-8 h-1 bg-bb-gold rounded-full"></div>
                  <div className="text-[8px] font-mono text-gray-500">BURN RATE</div>
                </div>
                <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden mb-2">
                  <div className="h-full w-2/3 bg-bb-gold"></div>
                </div>
                <div className="text-xs font-mono text-white">$1,420.00</div>
             </div>
          </GlassPanel>
        </div>
      </section>

      {/* 2. Demo / How It Works (Repaired Flow) */}
      <section className="space-y-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 px-4">
          <div className="space-y-2">
            <h3 className="text-xs text-bb-accent font-mono uppercase tracking-[0.4em] mb-4">Demo / How It Works</h3>
            <h2 className="text-3xl font-bold text-white tracking-tight">The 7-Step Trajectory</h2>
          </div>
          <div className="flex gap-1.5">
            {steps.map((_, i) => (
              <div key={i} className={`h-1.5 w-8 rounded-full transition-all duration-500 ${i <= activeStep ? 'bg-bb-gold shadow-[0_0_8px_#D4AF37]' : 'bg-white/10'}`}></div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4 space-y-2.5">
             {steps.map((step, idx) => (
               <button
                 key={idx}
                 onClick={() => setActiveStep(idx)}
                 className={`w-full text-left p-4 rounded-2xl transition-all border flex items-center gap-4 group ${
                   activeStep === idx 
                    ? 'bg-bb-gold/10 border-bb-gold/30 text-white translate-x-2' 
                    : 'bg-bb-panel/30 border-transparent text-gray-500 hover:bg-white/5 hover:border-white/10'
                 }`}
               >
                 <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${activeStep === idx ? 'bg-bb-gold text-bb-dark shadow-[0_0_15px_#D4AF37]' : 'bg-bb-dark border border-white/10 group-hover:scale-110'}`}>
                   <SafeIcon icon={step.icon} size={18} />
                 </div>
                 <div className="flex-1 min-w-0">
                   <p className="text-[9px] font-mono opacity-50 uppercase">Step 0{idx + 1}</p>
                   <p className="text-sm font-bold truncate tracking-tight">{step.title}</p>
                 </div>
                 {activeStep === idx && <SafeIcon icon={FiArrowRight} className="text-bb-gold animate-pulse" />}
               </button>
             ))}
          </div>

          <GlassPanel className="lg:col-span-8 p-10 flex flex-col justify-center relative overflow-hidden min-h-[500px]">
             <div className="absolute inset-0 bg-gradient-to-br from-bb-gold/5 via-transparent to-bb-accent/5 opacity-50"></div>
             <AnimatePresence mode="wait">
               <motion.div
                 key={activeStep}
                 initial={{ opacity: 0, scale: 0.95, y: 20 }}
                 animate={{ opacity: 1, scale: 1, y: 0 }}
                 exit={{ opacity: 0, scale: 1.05, y: -20 }}
                 className="space-y-10 relative z-10"
               >
                 <div className="max-w-2xl">
                    <div className="inline-block px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[10px] font-mono text-gray-400 uppercase tracking-widest mb-6">
                      Module: {steps[activeStep].title}
                    </div>
                    <h4 className="text-4xl font-bold text-white mb-4 tracking-tight leading-tight">{steps[activeStep].title}</h4>
                    <p className="text-gray-400 text-xl leading-relaxed">{steps[activeStep].desc}</p>
                 </div>
                 
                 <div className="p-8 bg-black/40 border border-white/5 rounded-3xl shadow-2xl backdrop-blur-md">
                    {/* Visual Mockups for each step */}
                    {activeStep === 0 && (
                      <div className="flex items-center gap-10">
                        <div className="w-24 h-24 rounded-3xl bg-bb-gold/10 flex items-center justify-center text-bb-gold border border-bb-gold/20 shadow-2xl">
                          <SafeIcon icon={FiCpu} size={48} />
                        </div>
                        <div className="flex-1 space-y-4">
                          <div className="flex justify-between text-[10px] font-mono text-gray-500 uppercase">
                            <span>Encryption Progress</span>
                            <span>98%</span>
                          </div>
                          <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
                            <motion.div initial={{ width: 0 }} animate={{ width: '98%' }} className="h-full bg-bb-gold shadow-[0_0_10px_#D4AF37]"></motion.div>
                          </div>
                          <p className="text-xs text-emerald-400 font-mono">SECURE BRIDGE ESTABLISHED</p>
                        </div>
                      </div>
                    )}
                    {activeStep === 1 && (
                      <div className="grid grid-cols-2 gap-6">
                         {[1,2].map(i => (
                           <div key={i} className="p-5 bg-white/5 border border-white/10 rounded-2xl">
                              <div className="flex justify-between items-center mb-4">
                                <div className={`w-8 h-8 rounded-lg ${i===1 ? 'bg-emerald-500/10 text-emerald-500' : 'bg-bb-accent/10 text-bb-accent'} flex items-center justify-center`}>
                                  <SafeIcon icon={FiTarget} />
                                </div>
                                <div className="text-[10px] font-mono text-gray-500">ACTIVE</div>
                              </div>
                              <div className="h-1 w-full bg-white/5 rounded-full mb-2"></div>
                              <div className="h-1 w-2/3 bg-white/5 rounded-full"></div>
                           </div>
                         ))}
                      </div>
                    )}
                    {activeStep >= 2 && (
                       <div className="flex flex-col items-center justify-center py-8">
                          <div className="w-20 h-20 rounded-full border-2 border-dashed border-bb-gold/30 flex items-center justify-center relative animate-[spin_10s_linear_infinite]">
                             <SafeIcon icon={steps[activeStep].icon} size={32} className="text-bb-gold -rotate-[inherit]" />
                          </div>
                          <p className="mt-6 text-xs font-mono text-gray-500 uppercase tracking-widest">Interface Pre-rendered</p>
                       </div>
                    )}
                 </div>
               </motion.div>
             </AnimatePresence>
          </GlassPanel>
        </div>
      </section>

      {/* 3. Sample Data / Visual Gallery (Fixed Scaling) */}
      <section className="space-y-12">
        <div className="flex items-center gap-3">
          <span className="w-2 h-2 rounded-full bg-bb-gold"></span>
          <h3 className="text-xs text-bb-gold font-mono uppercase tracking-[0.4em]">Sample Telemetry</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
           {/* Ledger Mock */}
           <GlassPanel className="p-8 flex flex-col h-full">
             <div className="flex justify-between items-center mb-8">
               <h5 className="text-xs font-mono text-gray-500 uppercase tracking-widest">Global Ledger</h5>
               <SafeIcon icon={FiDatabase} size={16} className="text-bb-gold/40" />
             </div>
             <div className="space-y-6 flex-1">
               {mockTransactions.slice(0, 4).map(tx => (
                 <div key={tx.id} className="flex justify-between items-center group cursor-default">
                   <div className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-bb-accent/40 group-hover:bg-bb-accent transition-colors"></div>
                      <div>
                        <p className="text-sm font-bold text-white group-hover:text-bb-accent transition-colors">{tx.merchant}</p>
                        <p className="text-[10px] text-gray-600 font-mono">{tx.category}</p>
                      </div>
                   </div>
                   <span className={`text-xs font-mono font-bold ${tx.amount > 0 ? 'text-emerald-400' : 'text-white'}`}>
                     {tx.amount > 0 ? '+' : ''}{tx.amount.toFixed(2)}
                   </span>
                 </div>
               ))}
             </div>
           </GlassPanel>

           {/* Planets Mock */}
           <GlassPanel className="p-8 flex flex-col h-full bg-bb-gold/[0.02]">
             <h5 className="text-xs font-mono text-gray-500 uppercase tracking-widest mb-8">Savings Orbits</h5>
             <div className="space-y-8 flex-1 flex flex-col justify-center">
               {savingsGoals.slice(0, 2).map((goal, idx) => (
                 <div key={goal.id} className="relative">
                    <div className="flex justify-between items-end mb-3">
                      <div>
                        <p className="text-[10px] text-bb-gold font-mono uppercase tracking-widest mb-1">{goal.name}</p>
                        <p className="text-xl font-mono text-white leading-none">${goal.saved}</p>
                      </div>
                      <p className="text-[10px] text-gray-600 font-mono">Target: ${goal.target}</p>
                    </div>
                    <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                       <motion.div 
                        initial={{ width: 0 }}
                        animate={{ width: `${(goal.saved/goal.target)*100}%` }}
                        className={`h-full bg-gradient-to-r ${idx === 0 ? 'from-bb-gold to-yellow-600' : 'from-bb-accent to-blue-600'}`}
                       ></motion.div>
                    </div>
                 </div>
               ))}
             </div>
           </GlassPanel>

           {/* Mobile Preview CSS Mock */}
           <GlassPanel className="p-8 flex flex-col items-center justify-center bg-bb-accent/[0.02]">
              <div className="w-full h-full border border-white/5 rounded-3xl bg-bb-dark p-4 relative overflow-hidden">
                 <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-4 bg-bb-panel rounded-b-xl border-x border-b border-white/10"></div>
                 <div className="mt-8 space-y-4">
                    <div className="h-2 w-2/3 bg-white/5 rounded-full"></div>
                    <div className="h-24 w-full bg-white/5 rounded-2xl border border-white/5 flex items-center justify-center">
                       <BrandLogo size="sm" />
                    </div>
                    <div className="space-y-2">
                       <div className="h-1 w-full bg-white/5 rounded-full"></div>
                       <div className="h-1 w-5/6 bg-white/5 rounded-full"></div>
                    </div>
                 </div>
                 <div className="absolute bottom-4 left-4 right-4 flex justify-between">
                    <div className="w-6 h-6 rounded-lg bg-white/5"></div>
                    <div className="w-6 h-6 rounded-lg bg-bb-gold/20 border border-bb-gold/30"></div>
                    <div className="w-6 h-6 rounded-lg bg-white/5"></div>
                 </div>
              </div>
              <p className="mt-4 text-[10px] font-mono text-gray-600 uppercase tracking-widest">Responsive Node Preview</p>
           </GlassPanel>
        </div>
      </section>

      {/* 4. Simulated States (Fixed Interaction) */}
      <section className="space-y-12">
        <div className="flex items-center gap-3">
          <span className="w-2 h-2 rounded-full bg-bb-accent"></span>
          <h3 className="text-xs text-bb-accent font-mono uppercase tracking-[0.4em]">State Telemetry</h3>
        </div>

        <GlassPanel className="p-4 md:p-10">
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            <div className="w-full lg:w-1/3 grid grid-cols-2 lg:grid-cols-1 gap-3">
              {['success', 'warning', 'empty', 'loading'].map(state => (
                <button
                  key={state}
                  onClick={() => setSimulatedState(state)}
                  className={`px-5 py-4 rounded-2xl text-xs font-mono uppercase tracking-widest transition-all border text-left flex items-center justify-between group ${
                    simulatedState === state 
                      ? 'bg-bb-accent/15 border-bb-accent text-bb-accent shadow-[0_0_20px_rgba(0,240,255,0.1)]' 
                      : 'bg-bb-dark/50 border-white/5 text-gray-600 hover:border-white/20 hover:text-gray-400'
                  }`}
                >
                  {state} Mode
                  {simulatedState === state && <div className="w-1.5 h-1.5 rounded-full bg-bb-accent animate-ping"></div>}
                </button>
              ))}
            </div>

            <div className="flex-1 w-full min-h-[300px] border border-white/5 rounded-[2.5rem] bg-black/40 flex items-center justify-center p-12 text-center relative overflow-hidden">
               <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,240,255,0.05)_0%,transparent_70%)]"></div>
               <AnimatePresence mode="wait">
                 <motion.div
                   key={simulatedState}
                   initial={{ opacity: 0, scale: 0.9, rotate: -2 }}
                   animate={{ opacity: 1, scale: 1, rotate: 0 }}
                   exit={{ opacity: 0, scale: 1.1, rotate: 2 }}
                   transition={{ type: "spring", damping: 15 }}
                   className="space-y-8 relative z-10"
                 >
                   {simulatedState === 'success' && (
                     <>
                        <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-400 mx-auto border border-emerald-500/20 shadow-[0_0_30px_rgba(16,185,129,0.2)]">
                          <SafeIcon icon={FiCheckCircle} size={40} />
                        </div>
                        <div className="space-y-2">
                          <h4 className="text-2xl font-bold text-white tracking-tight">System Nominal</h4>
                          <p className="text-gray-400 max-w-xs mx-auto">All spending beams are within projected orbital trajectory.</p>
                        </div>
                     </>
                   )}
                   {simulatedState === 'warning' && (
                     <>
                        <div className="w-20 h-20 rounded-full bg-red-400/10 flex items-center justify-center text-red-400 mx-auto border border-red-400/20 shadow-[0_0_30px_rgba(248,113,113,0.2)]">
                          <SafeIcon icon={FiAlertCircle} size={40} />
                        </div>
                        <div className="space-y-2">
                          <h4 className="text-2xl font-bold text-white tracking-tight">Sector Over-Burn</h4>
                          <p className="text-gray-400 max-w-xs mx-auto text-red-400/80 font-medium">Critical deficit detected in "Recreation" sector beam.</p>
                        </div>
                     </>
                   )}
                   {simulatedState === 'empty' && (
                     <>
                        <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center text-gray-700 mx-auto border border-white/5">
                          <SafeIcon icon={FiTarget} size={40} />
                        </div>
                        <div className="space-y-2">
                          <h4 className="text-2xl font-bold text-gray-500 tracking-tight">No Active Beams</h4>
                          <p className="text-gray-600 max-w-xs mx-auto">Initialize your first sector calibration to begin telemetry.</p>
                        </div>
                     </>
                   )}
                   {simulatedState === 'loading' && (
                     <>
                        <div className="w-20 h-20 rounded-full border-4 border-bb-gold/10 border-t-bb-gold flex items-center justify-center text-bb-gold mx-auto animate-spin shadow-[0_0_40px_rgba(212,175,55,0.1)]">
                          <SafeIcon icon={FiActivity} size={32} />
                        </div>
                        <div className="space-y-2">
                          <h4 className="text-2xl font-bold text-white tracking-tight">Syncing Neural Link</h4>
                          <p className="text-gray-500 max-w-xs mx-auto font-mono text-[10px] uppercase">Retrieving encrypted liquidity packets...</p>
                        </div>
                     </>
                   )}
                 </motion.div>
               </AnimatePresence>
            </div>
          </div>
        </GlassPanel>
      </section>

      {/* 5. FAQ (Polished Accordion) */}
      <section className="space-y-12 max-w-4xl mx-auto">
        <div className="text-center space-y-4">
          <h3 className="text-xs text-bb-gold font-mono uppercase tracking-[0.5em]">Central Intelligence</h3>
          <h2 className="text-3xl font-bold text-white tracking-tight">Knowledge Base</h2>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <GlassPanel 
              key={i} 
              className={`overflow-hidden transition-all duration-500 ${openFaq === i ? 'bg-white/5 border-bb-gold/30' : 'hover:bg-white/[0.03] border-transparent'}`}
            >
              <button 
                className="w-full p-6 text-left flex justify-between items-center group"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${openFaq === i ? 'bg-bb-gold text-bb-dark' : 'bg-bb-dark text-gray-600 group-hover:text-gray-400'}`}>
                    <SafeIcon icon={FiHelpCircle} size={16} />
                  </div>
                  <h6 className="text-sm font-bold text-white tracking-tight">{faq.q}</h6>
                </div>
                <SafeIcon icon={FiChevronDown} className={`text-gray-600 transition-transform duration-500 ${openFaq === i ? 'rotate-180 text-bb-gold' : ''}`} />
              </button>
              <AnimatePresence>
                {openFaq === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <div className="px-20 pb-8 pt-2">
                       <p className="text-sm text-gray-400 leading-relaxed border-l-2 border-bb-gold/30 pl-6">{faq.a}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </GlassPanel>
          ))}
        </div>
      </section>

      {/* Branded Footer */}
      <footer className="pt-20 border-t border-white/5 text-center space-y-10">
        <div className="flex flex-col items-center gap-4">
           <BrandLogo size="lg" />
           <p className="text-xs text-gray-500 max-w-sm mx-auto leading-relaxed">
             A professional household liquidity tracking node. No investment advice, just high-fidelity organization.
           </p>
        </div>
        
        <div className="flex justify-center gap-6 text-gray-600 text-xs font-mono uppercase tracking-[0.2em]">
           <span className="hover:text-bb-gold transition-colors cursor-pointer">Security</span>
           <span className="hover:text-bb-gold transition-colors cursor-pointer">Protocol</span>
           <span className="hover:text-bb-gold transition-colors cursor-pointer">Archive</span>
        </div>

        <div className="pt-10 pb-6 opacity-30">
          <p className="text-[9px] font-mono text-gray-600 uppercase tracking-[0.4em]">
            Simulation Node Release v2.4.0 — Made by <span className="text-white">LingLink AI</span> and <span className="text-white">DigLab AI</span>
          </p>
        </div>
      </footer>

    </div>
  );
}